import * as React from 'react';
import * as acl from 'common/utils/acl';
import moment = require('moment');
import { Block, Col, Row } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';

import { ActionButton } from 'common/components/layout/Page/ActionButton';
import { AddEmployeeDialog } from './AddEmployeeDialog';
import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { Datetime } from 'common/components/Datetime';
import { InfiniteList } from 'common/components/InfiniteList';
import { Loading } from 'common/components/Loading';
import { Org } from 'common/api/models/Org';
import { OrgAccess } from 'common/api/models/OrgAccess';
import { RowActionButton } from 'common/components/RowActionButton';
import { Search } from 'common/components/Search';
import { Table } from 'reactstrap';
import { MemberLink } from 'common/components/link/MemberLink';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { UsersDialog } from '../Users/UsersDialog';
import { formatFullname } from 'common/utils/format';
import { getOrgAccess } from 'common/utils/acl';
import { toApiError } from 'common/utils/api';
import { Member } from 'common/api/models/Member';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Analytics } from 'common/utils/Analytics';
import { AssignContactDialog } from './AssignContactDialog';
import { CONTACT_TYPES } from '../../common/constants';
import FormGroup from 'reactstrap/lib/FormGroup';
import FormText from 'reactstrap/lib/FormText';
import { AddItemDialog } from './AddItemDialog';
import { EligibleItem, EligibleItemMapping } from 'common/api/models/EligibleItem';
import { PassFailDialog } from 'common/components/dialogs/PassFailDialog';
import { VoucherApi } from 'common/api/resources/Voucher';
import { DayDialog } from 'common/components/dialogs/DayDialog';

export interface OrgsEmployeesProps {
  appState: AppState;
  entityActions: EntityActions;
  org: Member;
  onEdit: (org: Member) => void;
}

export interface SimpleDialog {
  onConfirm: (arg?: any) => void;
  onCancel: () => void;
}

export interface OrgsEmployeesState {
  isLoading: boolean;
  error?: string;
  members: Member[];
  org?: Org;
  contactUser?: Member;
  editMember?: Member;
  searchQuery: string;
  newUser?: Member;
  editUser?: Member;
  promoteUser?: boolean;
  removeUser?: Member;
  eligibleUser?: Member;
  extendEligibleUser?: Member;
  itemUser?: Member;
  removeItemUser?: Member;
  selectedItem?: string;
  deactivateUser?: boolean;
  addUser?: boolean;
  passFailDialogSelect?: boolean;
  reassignDialogSelect: boolean;
  passFailDialogEmail?: string;
  voucherIdSelect?: string;
  cancelVoucherId?: string;
  activatedDate?: string;
  expireDate?: string;
  originalExpireDate?: string;
  orderId?: string;
  productId?: string;
  next?: string;
  eligibleItems: EligibleItem[];
  allProducts: EligibleItem[];
}

export class OrgsEmployees extends React.Component<OrgsEmployeesProps, OrgsEmployeesState> {
  state: OrgsEmployeesState = {
    isLoading: false,
    members: [],
    searchQuery: '',
    eligibleItems: [],
    allProducts: [],
    reassignDialogSelect: false,
  };

  componentDidMount() {
    this.reload();
  }

  reload = (refresh?: boolean) => {
    this.props.entityActions.refetch(EntityType.MEMBER, this.props.org.id!);
    this.setState({ isLoading: true });
    const orgId = this.props.org.id || '';
    const parentOrgId = this.props.org.parentOrgId;
    const args = {
      orgMemberId: this.props.org.id,
      q: this.state.searchQuery + ' type:user',
      from: refresh ? undefined : this.state.next,
      voucher: 'voucher',
    };
    Analytics.member.search(args);
    Promise.all([
      MemberApi.findMembers(args),
      MemberApi.getEligibleItemsPerOrganization(parentOrgId || orgId),
      MemberApi.getAllProductsPerOrganization(parentOrgId || orgId),
    ]).then(([usersRes, eligibleItems, allProducts]) => {
      this.setState(prevState => ({
        ...prevState,
        members: prevState.next && !refresh ? prevState.members.concat(usersRes.data) : usersRes.data,
        next: usersRes.next,
        isLoading: false,
        eligibleItems,
        allProducts,
      }));
    }, this.handleError);
  };

  handleSearch = (query: string) => {
    this.setState({ searchQuery: query }, () => {
      this.reload(true);
    });
  };

  handleNew = () => {
    this.setState({
      newUser: {
        status: Member.Status.ACTIVE,
        chapterId: this.props.org.chapterId,
        workPhone: this.props.org.workPhone,
        workAddress: this.props.org.workAddress,
      },
    });
  };

  handleSaveNewUser = (id: string) => {
    this.setState({ newUser: undefined });
    this.reload(true);
  };

  handleAdd = () => {
    this.setState({ addUser: true });
  };

  handleSaveAddUser = (id: string) => {
    const user = this.props.appState.entities.member[id];
    MemberApi.inviteUser({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      chapterId: this.props.org.chapterId,
      orgs: [{ memberId: this.props.org.id!, access: OrgAccess.Access.NORMAL }],
    }).then(res => {
      Analytics.member.invite();
      this.setState({ addUser: undefined }, () => this.reload());
    }, this.handleError);
  };

  handleEdit = (user: Member) => {
    const access = getOrgAccess(user, this.props.org.id!);
    this.setState({ editUser: user, promoteUser: access !== OrgAccess.Access.ADMIN });
  };

  handleCancelVoucher = () => {
    VoucherApi.cancelVoucher(this.state.cancelVoucherId ? this.state.cancelVoucherId : '')
      .then(() => {
        this.setState({ cancelVoucherId: undefined });
        this.reload();
      })
      .catch(e => {
        this.setState({ error: e.response.data.message });
        this.setState({ isLoading: false });
      });
  };
  handleConfirmDeactivateLearningProgram = () => {
    VoucherApi.updateVoucher(this.state.voucherIdSelect ? this.state.voucherIdSelect : '', {
      isCanceled: true,
    })
      .then(() => {
        this.setState({ eligibleUser: undefined });
        this.reload();
      })
      .catch(e => {
        this.setState({ isLoading: false });
      });
  };

  handleConfirmEdit = () => {
    MemberApi.updateMember(this.state.editUser!.id!, {
      orgs: [
        {
          memberId: this.props.org.id!,
          access: this.state.promoteUser ? OrgAccess.Access.ADMIN : OrgAccess.Access.NORMAL,
        },
      ],
    }).then(res => {
      if (this.state.promoteUser) {
        Analytics.member.promoteEmployee();
      } else {
        Analytics.member.demoteEmployee();
      }
      this.setState({ editUser: undefined });
      this.reload();
    }, this.handleError);
  };
  handlePassFailDialog = () => {
    this.setState({ passFailDialogSelect: false, voucherIdSelect: undefined, passFailDialogEmail: undefined });
    this.reload(true);
  };
  handleExtendLearningProgram = () => {
    this.setState({
      extendEligibleUser: undefined,
      voucherIdSelect: undefined,
      orderId: undefined,
      productId: undefined,
    });
    this.reload(true);
  };
  handleRemove = (user: Member) => {
    this.setState({ removeUser: user });
  };
  handleCancelEligibleDialog = (user: Member, voucherId: any) => {
    this.setState({ eligibleUser: user, voucherIdSelect: voucherId });
  };
  handleCancelVoucherDialog = (user: Member, voucherId: any) => {
    this.setState({ cancelVoucherId: voucherId });
  };

  handleExtendEligibleDialog = (
    user: Member,
    voucherId: any,
    expireDate: any,
    originalExpireDate: any,
    orderId: string,
    productId: string
  ) => {
    this.setState({
      extendEligibleUser: user,
      voucherIdSelect: voucherId,
      expireDate: expireDate,
      originalExpireDate: originalExpireDate,
      orderId: orderId,
      productId: productId,
    });
  };

  handleCancelDialog = () => {
    this.setState({
      addUser: undefined,
      newUser: undefined,
      editUser: undefined,
      removeUser: undefined,
      contactUser: undefined,
      itemUser: undefined,
      removeItemUser: undefined,
      eligibleUser: undefined,
      extendEligibleUser: undefined,
      selectedItem: undefined,
      passFailDialogSelect: false,
      reassignDialogSelect: false,
      passFailDialogEmail: undefined,
      voucherIdSelect: undefined,
      activatedDate: undefined,
      expireDate: undefined,
      orderId: undefined,
      productId: undefined,
      cancelVoucherId: undefined,
    });
  };

  handleError = (err: Error) => this.setState({ error: toApiError(err), isLoading: false });

  handleConfirmRemove = () => {
    MemberApi.updateMember(this.state.removeUser!.id!, {
      ...(this.state.deactivateUser ? { status: Member.Status.INACTIVE } : {}),
      orgs: [{ memberId: this.props.org.id!, access: OrgAccess.Access.NONE }],
    }).then(res => {
      Analytics.member.removeEmployee();
      this.setState({ removeUser: undefined });
      this.reload();
    }, this.handleError);
  };

  handleVoucherReassign = () => {
    VoucherApi.reassignVoucher(this.state.voucherIdSelect ? this.state.voucherIdSelect : '', {}).then(res => {
      this.setState({ voucherIdSelect: undefined, reassignDialogSelect: false });
      this.reload(true);
    }, this.handleError);
  };
  handleAssignContact = (user: Member) => {
    this.setState({ contactUser: user });
  };

  handleAddItem = (itemUser: Member) => {
    this.setState({ itemUser });
  };

  handleConfirmAssignContact = () => {
    this.setState({ contactUser: undefined }, () => {
      this.reload(true);
    });
  };

  handleConfirmAddItem = (items: Object) => {
    const newEligibleOrderProducts: EligibleItemMapping[] = Object.keys(items)
      .map(key => {
        return {
          id: '',
          productId: key,
          orderId: items[key],
          activatedDate: moment().toISOString(),
        };
      })
      .filter(item => !!item.orderId);

    // give them a voucher for this product eligibility
    let index = 0;
    let key1 = 'productId';
    let key2 = 'orderId';
    while (index < newEligibleOrderProducts.length) {
      let pid = newEligibleOrderProducts[index][key1];
      let oid = newEligibleOrderProducts[index][key2];
      if (this.state.itemUser && this.state.itemUser.id) {
        // const expireDate = moment()
        //   .add(44, 'days') // DEV-1359: Allow 45 days instead of 28
        //   .toISOString();
        const todaysDate = moment().toISOString();
        VoucherApi.createVoucher(this.state.itemUser.id!, {
          memberId: this.state.itemUser.id!,
          orgId: this.props.org!.id!,
          productId: pid,
          orderId: oid,
          activatedDate: todaysDate,
          originalExpireDate: todaysDate,
          expireDate: todaysDate,
        })
          .then(resp => {
            this.handleCancelDialog();
            this.reload(true);
          })
          .catch(this.handleError);
      }
      index += 1;
    }
  };

  handleRemoveItem = (removeItemUser: Member, selectedItem: string) => () => {
    this.setState({ removeItemUser, selectedItem });
  };

  handleConfirmRemoveItem = () => {
    let vouchers = this.state.removeItemUser!.vouchers;
    let voucherId = '';
    vouchers!.forEach(voucher => {
      if (voucher.productId === this.state.selectedItem) {
        if (voucher.id) {
          voucherId = voucher.id;
        }
      }
    });

    VoucherApi.deleteVoucher(voucherId, {
      memberId: this.state.removeItemUser!.id!,
    })
      .then(resp => {
        this.handleCancelDialog();
        this.reload(true);
      })
      .catch(this.handleError);
  };

  findItemName = (id: string) => {
    const product =
      this.state.allProducts && this.state.allProducts.length
        ? this.state.allProducts.find(item => item.productId === id)
        : undefined;
    return product && product.productName;
  };

  render() {
    const org = this.props.appState.entities.member[this.props.org.id!];
    return (
      <Block marginBottom="2rem">
        <Col
          css={{
            '@media "(min-width: 1024px)"': {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
          margin="1rem 0"
        >
          <Row
            flex="1"
            css={{
              '@media "(min-width: 1024px)"': {
                marginBottom: 0,
                marginRight: '1rem',
              },
            }}
            marginBottom="1rem"
          >
            <Search placeholder="Search employees" initialValue="" onSubmit={this.handleSearch} />
          </Row>
          {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) && (
            <Row>
              <Block marginRight="1rem">
                <ActionButton label="Add Existing User" iconName="user" onClick={this.handleAdd} />
              </Block>
              <Block>
                <ActionButton label="New User" iconName="user-plus" onClick={this.handleNew} />
              </Block>
            </Row>
          )}
        </Col>

        <InfiniteList hasMore={!!this.state.next} isLoading={this.state.isLoading} onLoadMore={this.reload}>
          <Table responsive={true} hover={true} striped={true}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Access</th>
                <th>Contact</th>
                <th>Last Login</th>
                <th>Added Eligible Items</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!this.state.isLoading &&
                !this.state.error &&
                this.state.members.length === 0 && (
                  <tr>
                    <td colSpan={4}>No results found.</td>
                  </tr>
                )}
              {this.state.error && (
                <tr>
                  <td colSpan={6}>
                    <div className="alert alert-danger">{this.state.error}</div>
                  </td>
                </tr>
              )}
              {this.state.members.map((u, i) => {
                return (
                  <tr key={`employees${u.id}--${i}`}>
                    <td>
                      <MemberLink member={u} />
                    </td>
                    <td>
                      {getOrgAccess(u, org.id!) === OrgAccess.Access.ADMIN && <Badge type="primary" text="Admin" />}
                      {getOrgAccess(u, org.id!) === OrgAccess.Access.NORMAL && (
                        <span className="badge badge-pill" style={{ background: '#aaa' }}>
                          Member
                        </span>
                      )}
                    </td>
                    <td>
                      {org.contacts &&
                        CONTACT_TYPES.filter(c => org.contacts!![c.value] === u.id).map(c => (
                          <span
                            key={c.value}
                            className="badge badge-pill"
                            style={{ backgroundColor: c.color, marginRight: 5 }}
                          >
                            {c.label}
                          </span>
                        ))}
                    </td>
                    <td>
                      <Datetime at={u.loginAt} />
                    </td>
                    <td>
                      {u.vouchers &&
                        u.vouchers.map(item => (
                          <div>
                            <span key={item.productId} className="badge badge-pill badge-primary mr-1">
                              <span>{this.findItemName(item.productId)}</span>
                              {item.tagId === undefined && (
                                <>
                                  <i
                                    style={{ cursor: 'pointer' }}
                                    onClick={this.handleRemoveItem(u, item.productId)}
                                    className="fa fa-times ml-2"
                                  />
                                </>
                              )}
                              {item.tagId !== undefined &&
                                item.isPassed === undefined &&
                                item.voucherCode !== undefined && (
                                  <>
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        this.setState({
                                          passFailDialogSelect: true,
                                          passFailDialogEmail: u && u.email ? u.email : '',
                                          voucherIdSelect: item.id,
                                        });
                                        // tslint:disable-next-line: jsx-alignment
                                      }}
                                    >
                                      <i className="fa fa-check ml-2" />
                                      <span> Mark as pass / fail</span>
                                    </span>
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        this.handleExtendEligibleDialog(
                                          u,
                                          item.id,
                                          item.expireDate,
                                          item.originalExpireDate,
                                          item.orderId,
                                          item.productId
                                        )
                                      }
                                    >
                                      <i className="fa fa-arrow-circle-right ml-2" />
                                      <span> Extend</span>
                                    </span>
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => this.handleCancelEligibleDialog(u, item.id)}
                                    >
                                      <i className="fa fa-ban ml-2" />
                                      <span> Cancel</span>
                                    </span>
                                  </>
                                )}
                              {item.kryterionExams === undefined &&
                                item.voucherCode !== undefined &&
                                item.isPassed !== undefined &&
                                !item.isPassed && (
                                  <>
                                    <span
                                      style={{
                                        margin: '5px',
                                        borderRadius: '5px',
                                        background: 'red',
                                        paddingLeft: '5px',
                                        paddingRight: '5px',
                                      }}
                                    >
                                      Failed
                                    </span>
                                    <span
                                      style={{
                                        margin: '5px',
                                        borderRadius: '5px',
                                        background: 'orange',
                                        paddingLeft: '5px',
                                        paddingRight: '5px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        this.setState({
                                          reassignDialogSelect: true,
                                          voucherIdSelect: item.id,
                                        });
                                      }}
                                    >
                                      Reassign
                                    </span>
                                  </>
                                )}
                              {item.kryterionExams !== undefined &&
                                item.kryterionExams[item.kryterionExams.length - 1] &&
                                item.kryterionExams[item.kryterionExams.length - 1].passFail &&
                                item.kryterionExams[item.kryterionExams.length - 1].passFail === 'FAIL' && (
                                  <>
                                    <span
                                      style={{
                                        margin: '5px',
                                        borderRadius: '5px',
                                        background: 'red',
                                        paddingLeft: '5px',
                                        paddingRight: '5px',
                                      }}
                                    >
                                      Failed ({item.kryterionExams.length}x)
                                    </span>
                                  </>
                                )}
                              {item.kryterionExams !== undefined &&
                                item.kryterionExams.length > 0 &&
                                item.kryterionExams[item.kryterionExams.length - 1].status === 'SCHEDULED' && (
                                  <>
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => this.handleCancelVoucherDialog(u, item.id)}
                                    >
                                      <i className="fa fa-ban ml-2" />
                                      <span> Cancel</span>
                                    </span>
                                  </>
                                )}
                              {item.kryterionExams !== undefined &&
                                item.kryterionExams[item.kryterionExams.length - 1] &&
                                item.kryterionExams[item.kryterionExams.length - 1].passFail &&
                                item.kryterionExams[item.kryterionExams.length - 1].passFail === 'FAIL' &&
                                item.kryterionExams.length > 1 && (
                                  <>
                                    <span
                                      style={{
                                        margin: '5px',
                                        borderRadius: '5px',
                                        background: 'orange',
                                        paddingLeft: '5px',
                                        paddingRight: '5px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        this.setState({
                                          reassignDialogSelect: true,
                                          voucherIdSelect: item.id,
                                        });
                                      }}
                                    >
                                      Reassign
                                    </span>
                                  </>
                                )}
                              {item.kryterionExams !== undefined &&
                                item.kryterionExams[item.kryterionExams.length - 1] && (
                                  <>
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        this.handleExtendEligibleDialog(
                                          u,
                                          item.id,
                                          item.expireDate,
                                          item.originalExpireDate,
                                          item.orderId,
                                          item.productId
                                        )
                                      }
                                    >
                                      <i className="fa fa-arrow-circle-right ml-2" />
                                      <span> Extend</span>
                                    </span>
                                    <span
                                      style={{
                                        margin: '5px',
                                        borderRadius: '5px',
                                        paddingLeft: '5px',
                                        paddingRight: '5px',
                                      }}
                                    >
                                      - {item.kryterionExams[item.kryterionExams.length - 1].status}
                                    </span>
                                  </>
                                )}
                            </span>
                          </div>
                        ))}
                    </td>
                    {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) && (
                      <td>
                        <RowActionButton
                          text={
                            getOrgAccess(u, org.id!) === OrgAccess.Access.ADMIN ? 'Convert to Member' : 'Make an Admin'
                          }
                          item={u}
                          onClick={this.handleEdit}
                        />{' '}
                        <RowActionButton
                          text="Assign as Contact"
                          icon="star"
                          item={u}
                          onClick={this.handleAssignContact}
                        />{' '}
                        {(u.vouchers ? u.vouchers.length : 0) < this.state.eligibleItems.length && (
                          <>
                            <RowActionButton
                              text="Assign Item"
                              icon="certificate"
                              item={u}
                              onClick={this.handleAddItem}
                            />{' '}
                          </>
                        )}
                        <RowActionButton text="Remove from Org" icon="times" item={u} onClick={this.handleRemove} />
                      </td>
                    )}
                  </tr>
                );
              })}
              {this.state.isLoading && (
                <tr>
                  <td colSpan={4}>
                    <Loading />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </InfiniteList>

        <ConfirmDialog
          title="Deactivate User for Learning Program!"
          message={`Are you sure you want to remove ${formatFullname(this.state.eligibleUser)} from program?`}
          isOpen={!!this.state.cancelVoucherId}
          onConfirm={this.handleCancelVoucher}
          onCancel={this.handleCancelDialog}
          error={this.state.error}
          loadingMessage="Sending..."
        />

        <ConfirmDialog
          title="Deactivate User for Learning Program"
          message={`Are you sure you want to remove ${formatFullname(this.state.eligibleUser)} from program?`}
          isOpen={!!this.state.eligibleUser}
          onConfirm={this.handleConfirmDeactivateLearningProgram}
          onCancel={this.handleCancelDialog}
          loadingMessage="Sending..."
        />

        <ConfirmDialog
          isOpen={!!this.state.removeUser}
          title="Remove From Organization"
          message={
            <div>
              {'Are you sure you want to remove ' + formatFullname(this.state.removeUser) + ' from the organization?'}
              {this.state.removeUser &&
                this.state.removeUser.orgs &&
                this.state.removeUser.orgs.length === 1 && (
                  <FormGroup className="mt-3">
                    <label>
                      <input
                        type="checkbox"
                        id="deactivate"
                        checked={this.state.deactivateUser}
                        onChange={() => {
                          this.setState(({ deactivateUser }) => ({ deactivateUser: !deactivateUser }));
                        }}
                      />{' '}
                      Deactivate user
                    </label>
                    <FormText>Select this option to additionally set the user's status to "inactive."</FormText>
                  </FormGroup>
                )}
            </div>
          }
          onConfirm={this.handleConfirmRemove}
          onCancel={this.handleCancelDialog}
        />
        <ConfirmDialog
          isOpen={!!this.state.editUser && !!this.state.promoteUser}
          title="Promote to Admin"
          message={'Are you sure you want to promote ' + formatFullname(this.state.editUser) + ' to an admin?'}
          onConfirm={this.handleConfirmEdit}
          onCancel={this.handleCancelDialog}
        />
        <ConfirmDialog
          isOpen={!!this.state.editUser && !this.state.promoteUser}
          title="Demote from Admin"
          message={'Are you sure you want to convert ' + formatFullname(this.state.editUser) + ' to a non-admin?'}
          onConfirm={this.handleConfirmEdit}
          onCancel={this.handleCancelDialog}
        />
        <ConfirmDialog
          isOpen={!!this.state.removeItemUser && !!this.state.selectedItem}
          title="Remove added Eligible Item"
          message={`Are you sure you want to remove the eligible item ${this.findItemName(
            this.state.selectedItem!
          )} from ${formatFullname(this.state.removeItemUser)}`}
          onConfirm={this.handleConfirmRemoveItem}
          onCancel={this.handleCancelDialog}
        />
        <UsersDialog
          user={this.state.newUser}
          appState={this.props.appState}
          onSave={this.handleSaveNewUser}
          onCancel={this.handleCancelDialog}
          addOrgId={this.props.org.id}
          segment={this.props.org.segment}
        />
        <AddEmployeeDialog
          isOpen={this.state.addUser}
          appState={this.props.appState}
          entityActions={this.props.entityActions}
          onSave={this.handleSaveAddUser}
          onCancel={this.handleCancelDialog}
        />
        <AssignContactDialog
          org={org}
          user={this.state.contactUser}
          onConfirm={this.handleConfirmAssignContact}
          onCancel={this.handleCancelDialog}
        />
        <AddItemDialog
          user={this.state.itemUser}
          items={this.state.eligibleItems || []}
          onConfirm={this.handleConfirmAddItem}
          onCancel={this.handleCancelDialog}
        />
        <PassFailDialog
          isOpen={this.state.passFailDialogSelect}
          email={this.state.passFailDialogEmail ? this.state.passFailDialogEmail : ''}
          voucher={this.state.voucherIdSelect ? this.state.voucherIdSelect : ''}
          onCancel={this.handleCancelDialog}
          onConfirm={this.handlePassFailDialog}
        />
        <DayDialog
          title="Extend Eligibility"
          isOpen={!!this.state.extendEligibleUser}
          voucher={this.state.voucherIdSelect ? this.state.voucherIdSelect : ''}
          member={this.state.extendEligibleUser}
          expireDate={this.state.expireDate}
          originalExpireDate={this.state.originalExpireDate ? this.state.originalExpireDate : ''}
          orderId={this.state.orderId}
          productId={this.state.productId}
          onSave={this.handleExtendLearningProgram}
          onCancel={this.handleCancelDialog}
        />
        <ConfirmDialog
          isOpen={this.state.reassignDialogSelect}
          title="Reassign Badge Exam"
          message="Are you sure you want to reassign this badge exam to this user?"
          onConfirm={this.handleVoucherReassign}
          onCancel={this.handleCancelDialog}
        />
      </Block>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/OrgsEmployees.tsx